import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import './GlobalSearch.css';

interface SearchResult {
  id: string;
  label: string;
  sublabel?: string;
  type: 'project' | 'folder' | 'testrun';
  path: string;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  project: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
    </svg>
  ),
  folder: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
    </svg>
  ),
  testrun: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>
    </svg>
  ),
};

const TYPE_LABELS: Record<string, string> = {
  project: 'Project',
  folder: 'Folder',
  testrun: 'Test Run',
};

export const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const term = q.toLowerCase();

      // Fetch in parallel
      const [tcFolders, scFolders, runs] = await Promise.allSettled([
        apiClient.getFolders('test_case'),
        apiClient.getFolders('test_scenario'),
        apiClient.listTestRuns(),
      ]);

      const found: SearchResult[] = [];

      // Projects (level 0 test_case folders)
      if (tcFolders.status === 'fulfilled') {
        tcFolders.value
          .filter(f => f.level === 0 && f.name.toLowerCase().includes(term))
          .forEach(f => found.push({
            id: f.id,
            label: f.name,
            sublabel: f.description || undefined,
            type: 'project',
            path: `/folders/${f.id}`,
          }));
      }

      // Subfolders (level > 0, both types)
      const allFolders = [
        ...(tcFolders.status === 'fulfilled' ? tcFolders.value : []),
        ...(scFolders.status === 'fulfilled' ? scFolders.value : []),
      ];
      allFolders
        .filter(f => f.level > 0 && f.name.toLowerCase().includes(term))
        .forEach(f => {
          // avoid duplicates
          if (!found.find(r => r.id === f.id)) {
            found.push({
              id: f.id,
              label: f.name,
              sublabel: f.folder_type === 'test_scenario' ? 'Test Scenario Folder' : 'Test Case Folder',
              type: 'folder',
              path: `/folders/${f.id}`,
            });
          }
        });

      // Test Runs
      if (runs.status === 'fulfilled') {
        (runs.value as any[])
          .filter((r: any) => r.name.toLowerCase().includes(term))
          .forEach((r: any) => found.push({
            id: r.id,
            label: r.name,
            sublabel: `${r.status} · ${r.run_type}`,
            type: 'testrun',
            path: `/test-runs/${r.id}`,
          }));
      }

      setResults(found.slice(0, 12));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => {
      search(query);
      setOpen(true);
      setActiveIdx(-1);
    }, 280);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, search]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (result: SearchResult) => {
    navigate(result.path);
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const highlight = (text: string, q: string) => {
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1 || !q) return <>{text}</>;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="gs-highlight">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  return (
    <div className="gs-wrap" ref={wrapRef}>
      <svg className="gs-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input
        ref={inputRef}
        className="gs-input"
        type="text"
        placeholder="Search projects, tests or documentation..."
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {query && (
        <button className="gs-clear" onClick={() => { setQuery(''); setResults([]); setOpen(false); }}>×</button>
      )}

      {open && (
        <div className="gs-dropdown">
          {loading ? (
            <div className="gs-state">Searching...</div>
          ) : results.length === 0 ? (
            <div className="gs-state">No results for "<strong>{query}</strong>"</div>
          ) : (
            <>
              <div className="gs-count">{results.length} result{results.length !== 1 ? 's' : ''}</div>
              {results.map((r, i) => (
                <button
                  key={r.id + r.type}
                  className={`gs-item${i === activeIdx ? ' gs-item-active' : ''}`}
                  onMouseDown={() => handleSelect(r)}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <span className={`gs-type-icon gs-icon-${r.type}`}>{TYPE_ICONS[r.type]}</span>
                  <div className="gs-item-text">
                    <span className="gs-item-label">{highlight(r.label, query)}</span>
                    {r.sublabel && <span className="gs-item-sub">{r.sublabel}</span>}
                  </div>
                  <span className="gs-type-badge">{TYPE_LABELS[r.type]}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
